import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Icon,
  Input,
  NumberInput,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Text,
  useToast,
} from "@app/ui";
import type { PassportSchemaDef } from "@app/dpp-schemas";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import {
  applySupplierSubmission,
  createSupplierInvite,
  getSupplierSubmission,
  listSupplierInvites,
  revokeSupplierInvite,
} from "../api/dpp.js";
import { useLang, useT, useFormatDateTime } from "../i18n.js";
import { fieldLabel } from "./schema-registry.js";
import type { SupplierInvite, SupplierInviteStatus, SupplierSubmissionReview } from "../types.js";

/**
 * "Suppliers" tab of the product detail page: an inline create-invite panel
 * (spacious multi-column field picker instead of a cramped modal), the invite
 * list, one-time share-link display, and submission review in a side Drawer
 * (per-field diff + selective apply) + revoke.
 *
 * The raw share token exists ONLY in the create response — the API stores just
 * its hash. Share URLs are kept in component memory for the session: the copy
 * button works right after creation; older invites honestly say the link cannot
 * be re-shown.
 */

const STATUS_COLOR: Record<SupplierInviteStatus, "default" | "primary" | "success" | "warning" | "error"> = {
  pending: "warning",
  submitted: "primary",
  applied: "success",
  revoked: "default",
  expired: "error",
};

export interface SupplierInvitesCardProps {
  orgId: string;
  productId: string;
  def: PassportSchemaDef | undefined;
  /** Called after a submission is applied so the parent reloads the product. */
  onApplied: () => void;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

export function SupplierInvitesCard({ orgId, productId, def, onApplied }: SupplierInvitesCardProps) {
  const t = useT();
  const { lang } = useLang();
  const fmtDate = useFormatDateTime();
  const toast = useToast();
  const errorText = useErrorText();

  const [invites, setInvites] = useState<SupplierInvite[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  /** inviteId → full share URL; session-only (tokens are shown once). */
  const [shareUrls, setShareUrls] = useState<Record<string, string>>({});

  // Inline create-invite panel
  const [createOpen, setCreateOpen] = useState(false);
  const [supplierName, setSupplierName] = useState("");
  const [supplierEmail, setSupplierEmail] = useState("");
  const [expiresDays, setExpiresDays] = useState("30");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  // Review drawer
  const [reviewOpen, setReviewOpen] = useState(false);
  const [review, setReview] = useState<SupplierSubmissionReview | null>(null);
  const [reviewError, setReviewError] = useState<unknown>(null);
  const [applySelected, setApplySelected] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);

  const totalFields = useMemo(
    () => (def ? def.categories.reduce((n, c) => n + c.fields.length, 0) : 0),
    [def],
  );

  const load = async () => {
    setError(null);
    try {
      const page = await listSupplierInvites(orgId, { productId, limit: 100 });
      setInvites(page.items);
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    void load();
  }, [orgId, productId]);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("suppliers.linkCopied"));
    } catch {
      toast.error(t("error.actionFailed"));
    }
  };

  const resetCreate = () => {
    setSupplierName("");
    setSupplierEmail("");
    setExpiresDays("30");
    setNote("");
    setSelected(new Set());
    setCreateError(null);
    setCreatedUrl(null);
  };

  const openCreate = () => {
    resetCreate();
    setCreateOpen(true);
  };

  const toggleField = (fieldId: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(fieldId);
      else next.delete(fieldId);
      return next;
    });
  };

  const toggleCategory = (fieldIds: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of fieldIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const selectAll = (checked: boolean) => {
    if (!def) return;
    setSelected(checked ? new Set(def.categories.flatMap((c) => c.fields.map((f) => f.id))) : new Set());
  };

  const submitCreate = async () => {
    if (selected.size === 0) {
      setCreateError(t("suppliers.selectFieldRequired"));
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      const days = Number(expiresDays);
      const result = await createSupplierInvite(orgId, productId, {
        supplierName: supplierName.trim(),
        ...(supplierEmail.trim() ? { supplierEmail: supplierEmail.trim() } : {}),
        fieldIds: [...selected],
        ...(Number.isFinite(days) && days > 0 ? { expiresInDays: days } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      // The token is only ever available HERE — build the share URL now.
      const url = `${window.location.origin}${result.sharePath}`;
      setShareUrls((prev) => ({ ...prev, [result.invite.id]: url }));
      setCreatedUrl(url);
      toast.success(t("suppliers.created"));
      await load();
    } catch (e) {
      setCreateError(e);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (inviteId: string) => {
    try {
      await revokeSupplierInvite(orgId, inviteId);
      toast.success(t("suppliers.revoked"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  const openReview = async (inviteId: string) => {
    setReview(null);
    setReviewError(null);
    setReviewOpen(true);
    try {
      const data = await getSupplierSubmission(orgId, inviteId);
      setReview(data);
      // Pre-select the fields that actually change the product.
      setApplySelected(new Set(data.diff.filter((d) => d.changed).map((d) => d.fieldId)));
    } catch (e) {
      setReviewError(e);
    }
  };

  const apply = async () => {
    if (!review) return;
    if (applySelected.size === 0) {
      setReviewError(t("suppliers.selectAtLeastOne"));
      return;
    }
    setReviewError(null);
    setApplying(true);
    try {
      await applySupplierSubmission(orgId, review.invite.id, [...applySelected]);
      toast.success(t("suppliers.applied"));
      setReviewOpen(false);
      await load();
      onApplied();
    } catch (e) {
      setReviewError(e);
    } finally {
      setApplying(false);
    }
  };

  const reviewable = (s: SupplierInviteStatus) => s === "submitted" || s === "applied";
  const revocable = (s: SupplierInviteStatus) => s === "pending" || s === "submitted" || s === "expired";
  const alreadyApplied = review?.invite.status === "applied";

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      {error != null && <ErrorAlert error={error} />}

      {/* Create invite — inline, roomy field picker (not a modal) */}
      {def && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          {!createOpen ? (
            <Button variant="primary" icon={<Icon name="plus" size={14} />} onClick={openCreate}>
              {t("suppliers.invite")}
            </Button>
          ) : (
            <Button
              variant="subtle"
              icon={<Icon name="close" size={14} />}
              onClick={() => setCreateOpen(false)}
            >
              {t("common.cancel")}
            </Button>
          )}
        </div>
      )}

      {def && createOpen && (
        <div
          style={{
            border: "1px solid var(--az-border)",
            borderRadius: "var(--az-radius-lg)",
            padding: "var(--az-sp-5)",
            background: "var(--az-surface)",
          }}
        >
          {createdUrl ? (
            <Space direction="vertical" size="md" style={{ width: "100%" }}>
              <Alert type="success">{t("suppliers.created")}</Alert>
              <Alert type="warning">{t("suppliers.linkOnce")}</Alert>
              <Input readOnly value={createdUrl} onFocus={(e) => e.target.select()} />
              <Space size="sm">
                <Button
                  variant="primary"
                  icon={<Icon name="copy" size={14} />}
                  onClick={() => void copy(createdUrl)}
                >
                  {t("suppliers.copyLink")}
                </Button>
                <Button onClick={openCreate}>{t("suppliers.invite")}</Button>
                <Button variant="subtle" onClick={() => setCreateOpen(false)}>
                  {t("common.close")}
                </Button>
              </Space>
            </Space>
          ) : (
            <Space direction="vertical" size="lg" style={{ width: "100%" }}>
              {createError != null && <ErrorAlert error={createError} />}
              {/* Meta — one roomy row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: "var(--az-sp-4)",
                }}
              >
                <Input
                  label={t("suppliers.name")}
                  required
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                />
                <Input
                  label={t("suppliers.email")}
                  help={t("suppliers.emailHelp")}
                  type="email"
                  value={supplierEmail}
                  onChange={(e) => setSupplierEmail(e.target.value)}
                />
                <NumberInput
                  label={t("suppliers.expiresInDays")}
                  help={t("suppliers.expiresHelp")}
                  min={1}
                  max={90}
                  decimals={0}
                  value={expiresDays}
                  onChange={setExpiresDays}
                />
                <Input label={t("suppliers.note")} value={note} onChange={(e) => setNote(e.target.value)} />
              </div>

              {/* Field picker — spacious, per-category select-all */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--az-sp-3)",
                    marginBottom: "var(--az-sp-2)",
                    flexWrap: "wrap",
                  }}
                >
                  <Text strong>
                    {t("suppliers.fields")}{" "}
                    <Text secondary>
                      ({selected.size}/{totalFields})
                    </Text>
                  </Text>
                  <Space size="sm">
                    <Button size="sm" variant="subtle" onClick={() => selectAll(true)}>
                      {t("suppliers.selectAll")}
                    </Button>
                    <Button size="sm" variant="subtle" onClick={() => selectAll(false)}>
                      {t("suppliers.clear")}
                    </Button>
                  </Space>
                </div>
                <Text small secondary>
                  {t("suppliers.fieldsHelp")}
                </Text>
                <Space direction="vertical" size="md" style={{ width: "100%", marginTop: "var(--az-sp-3)" }}>
                  {def.categories.map((category) => {
                    const ids = category.fields.map((f) => f.id);
                    const allChecked = ids.every((id) => selected.has(id));
                    return (
                      <div key={category.id}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--az-sp-2)",
                            paddingBottom: "var(--az-sp-1)",
                            borderBottom: "1px solid var(--az-border)",
                            marginBottom: "var(--az-sp-2)",
                          }}
                        >
                          <Checkbox
                            checked={allChecked}
                            onChange={(e) => toggleCategory(ids, e.target.checked)}
                          >
                            <Text strong small>
                              {category.labels[lang]}
                            </Text>
                          </Checkbox>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                            gap: "var(--az-sp-1) var(--az-sp-4)",
                          }}
                        >
                          {category.fields.map((field) => (
                            <Checkbox
                              key={field.id}
                              checked={selected.has(field.id)}
                              onChange={(e) => toggleField(field.id, e.target.checked)}
                            >
                              {field.labels[lang]}
                            </Checkbox>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </Space>
              </div>

              <Space size="sm">
                <Button variant="primary" loading={creating} onClick={() => void submitCreate()}>
                  {t("common.create")}
                </Button>
                <Button variant="subtle" onClick={() => setCreateOpen(false)}>
                  {t("common.cancel")}
                </Button>
              </Space>
            </Space>
          )}
        </div>
      )}

      {/* Invite list */}
      {invites === null ? (
        <Spin />
      ) : (
        <Table
          rowKey={(i) => i.id}
          empty={t("suppliers.empty")}
          columns={[
            {
              key: "supplier",
              title: t("suppliers.supplier"),
              render: (i: SupplierInvite) => (
                <Space direction="vertical" size="sm">
                  <span>{i.supplierName}</span>
                  {i.supplierEmail && (
                    <Text small secondary>
                      {i.supplierEmail}
                    </Text>
                  )}
                </Space>
              ),
            },
            {
              key: "fields",
              title: t("suppliers.fieldCount"),
              numeric: true,
              width: 96,
              render: (i: SupplierInvite) => i.fieldIds.length,
            },
            {
              key: "status",
              title: t("common.status"),
              width: 120,
              render: (i: SupplierInvite) => (
                <Tag color={STATUS_COLOR[i.status]}>{t(`supplierStatus.${i.status}`)}</Tag>
              ),
            },
            {
              key: "expiresAt",
              title: t("suppliers.expiry"),
              width: 168,
              render: (i: SupplierInvite) => fmtDate(i.expiresAt),
            },
            {
              key: "actions",
              title: t("common.actions"),
              render: (i: SupplierInvite) => (
                <Space size="sm">
                  {shareUrls[i.id] && i.status === "pending" && (
                    <Button
                      size="sm"
                      variant="subtle"
                      icon={<Icon name="copy" size={14} />}
                      onClick={() => void copy(shareUrls[i.id]!)}
                    >
                      {t("suppliers.copyLink")}
                    </Button>
                  )}
                  {reviewable(i.status) && (
                    <Button size="sm" onClick={() => void openReview(i.id)}>
                      {t("suppliers.review")}
                    </Button>
                  )}
                  {revocable(i.status) && (
                    <Popconfirm
                      title={t("suppliers.revokeConfirm")}
                      description={t("suppliers.revokeDescription")}
                      okType="danger"
                      okText={t("suppliers.revoke")}
                      cancelText={t("common.cancel")}
                      onConfirm={() => void revoke(i.id)}
                    >
                      <Button size="sm" variant="danger">
                        {t("suppliers.revoke")}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              ),
            },
          ]}
          data={invites}
        />
      )}

      {/* Review submission — side drawer (more room than a modal) */}
      <Drawer
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        title={t("suppliers.reviewTitle")}
        width={760}
        footer={
          review && !alreadyApplied ? (
            <Space size="sm" style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button variant="subtle" onClick={() => setReviewOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" loading={applying} onClick={() => void apply()}>
                {t("suppliers.apply")}
              </Button>
            </Space>
          ) : undefined
        }
      >
        <Space direction="vertical" size="md" style={{ width: "100%" }}>
          {reviewError != null && <ErrorAlert error={reviewError} />}
          {!review && !reviewError && <Spin />}
          {review && (
            <>
              <Space size="lg" wrap>
                <Text small secondary>
                  {t("suppliers.submittedBy")}: {review.submission.submitterName}
                </Text>
                <Text small secondary>
                  {t("suppliers.submittedAt")}: {fmtDate(review.submission.submittedAt)}
                </Text>
                {review.submission.appliedAt && (
                  <Text small secondary>
                    {t("suppliers.appliedAt")}: {fmtDate(review.submission.appliedAt)}
                  </Text>
                )}
              </Space>
              <Table
                rowKey={(d) => d.fieldId}
                columns={[
                  {
                    key: "select",
                    title: "",
                    width: 44,
                    render: (d) =>
                      alreadyApplied ? null : (
                        <Checkbox
                          checked={applySelected.has(d.fieldId)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setApplySelected((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(d.fieldId);
                              else next.delete(d.fieldId);
                              return next;
                            });
                          }}
                        />
                      ),
                  },
                  {
                    key: "field",
                    title: t("suppliers.field"),
                    render: (d) => fieldLabel(def, d.fieldId, lang),
                  },
                  {
                    key: "submitted",
                    title: t("suppliers.submittedValue"),
                    render: (d) => <Text code>{formatValue(d.submitted)}</Text>,
                  },
                  {
                    key: "current",
                    title: t("suppliers.currentValue"),
                    render: (d) => <Text code>{formatValue(d.current)}</Text>,
                  },
                  {
                    key: "changed",
                    title: t("common.status"),
                    width: 96,
                    render: (d) =>
                      d.changed ? (
                        <Tag color="warning">{t("suppliers.changed")}</Tag>
                      ) : (
                        <Tag>{t("suppliers.same")}</Tag>
                      ),
                  },
                ]}
                data={review.diff}
              />
            </>
          )}
        </Space>
      </Drawer>
    </Space>
  );
}
